import type { CtaLink } from "./types";

/**
 * The Sentience section of odingard.com (sentience.odingard.com redirects
 * here). The story is YC Request for Startups #12 as written, which is what
 * Sentience is built to: watches, files, flags, reports — autonomously, under
 * authority the customer records.
 */

export type SentiencePageKey =
  | "overview"
  | "watch"
  | "file"
  | "flag"
  | "report"
  | "pilot"
  | "privacy"
  | "terms";

export type SentiencePageDef = {
  key: SentiencePageKey;
  path: string;
  navLabel: string;
  title: string;
  lede: string;
  /** Legal pages route and prerender but stay out of the section menu. */
  inNav: boolean;
};

/** YC Request for Startups #12, AI-Native Compliance, as written. Sentience is built to it. */
export const RFS12 = {
  label: "AI-Native Compliance",
  sentence:
    "Software that watches regulations across all 50 states, files renewals, flags anomalies, and writes audit reports.",
  close: "It replaces the compliance team, not their spreadsheet.",
};

export const SENTIENCE_PAGES: SentiencePageDef[] = [
  { key: "overview", path: "/sentience", navLabel: "Overview", title: "Sentience — AI-native compliance", lede: `${RFS12.sentence} ${RFS12.close}`, inNav: true },
  { key: "watch", path: "/sentience/watch", navLabel: "Watches regulations", title: "Sentience — Watches regulations across all 50 states", lede: "Sentience watches the regulations of all 50 states and works out which of them apply to your entities, without anyone reading the bulletins.", inNav: true },
  { key: "file", path: "/sentience/file", navLabel: "Files renewals", title: "Sentience — Files renewals", lede: "Sentience prepares and files your renewals and reports on its own, under the filing authority you record once.", inNav: true },
  { key: "flag", path: "/sentience/flag", navLabel: "Flags anomalies", title: "Sentience — Flags anomalies", lede: "Sentience flags the conditions that create exposure — a lapsed registration, a crossed threshold, a missed window — while they can still be fixed.", inNav: true },
  { key: "report", path: "/sentience/report", navLabel: "Writes audit reports", title: "Sentience — Writes audit reports", lede: "Sentience writes the audit report from a tamper-evident record of everything it watched, decided and filed.", inNav: true },
  { key: "pilot", path: "/sentience/pilot", navLabel: "Founding pilot", title: "Sentience Founding Pilot — Apply for a 30-day pilot", lede: "Apply for a 30-day Sentience founding pilot on your own entities and states.", inNav: true },
  { key: "privacy", path: "/sentience/privacy", navLabel: "Privacy", title: "Sentience — Privacy", lede: "How Odingard handles information submitted through the Sentience pilot request form.", inNav: false },
  { key: "terms", path: "/sentience/terms", navLabel: "Terms", title: "Sentience — Terms", lede: "Terms of use for the Sentience pages of odingard.com.", inNav: false },
];

export const sentiencePage = (path: string) =>
  SENTIENCE_PAGES.find((p) => p.path === path);

export const SENTIENCE_PILOT_PATH = "/sentience/pilot";

export const SENTIENCE_HERO = {
  eyebrow: "Sentience by Odingard Security · AI-native compliance",
  headline: "It replaces the compliance team, not their spreadsheet.",
  lede: RFS12.sentence,
  autonomy: "Sentience acts on its own, under the authority you set once. You stay informed; you don't do the work.",
  primaryCta: { label: "Apply for a Founding Pilot", href: SENTIENCE_PILOT_PATH } satisfies CtaLink,
};

/** The four verbs of RFS #12, in the order Sentience works. */
export const SENTIENCE_VERBS = [
  {
    key: "watch",
    verb: "Watches regulations across all 50 states",
    you: "You hear about a rule change before it costs you, not at the audit.",
    body: "Sentience keeps every state's statutes, agency rules and filing calendars under watch, keeps each version it reads, detects what changed, and determines whether the change reaches any of your entities.",
    points: [
      "Every source version retained, so a change is a difference between two copies, not an opinion",
      "Applicability worked out per entity and state from your recorded footprint",
      "Every resulting duty created with its due date and its citation",
    ],
  },
  {
    key: "file",
    verb: "Files renewals",
    you: "You know the May 15 deadline exists — and that it's handled — before it becomes a penalty.",
    body: "Sentience prepares the renewal or report a duty implies, checks it against the filing authority you recorded for that state, files it through the agency's own channel, and closes the duty against the receipt.",
    points: [
      "Record filing authority once per state and filing type, with a scope and an expiry",
      "Sentience files on its own inside that authority, and refuses anything outside it",
      "Every receipt is kept and the duty is closed against it",
    ],
  },
  {
    key: "flag",
    verb: "Flags anomalies",
    you: "You find out a registration lapsed while it can still be fixed.",
    body: "Sentience reads the state registries and your operating signals for the conditions that create exposure — a lapsed registration, a crossed nexus threshold, a filing window opening against an entity — and raises each one with its consequence.",
    points: [
      "Anomalies checked against the public record for each of your entities",
      "Each one tied to the duty or authority it puts at risk",
      "A condition that clears on its own resolves on its own",
    ],
  },
  {
    key: "report",
    verb: "Writes audit reports",
    you: "When the auditor asks, the answer already exists.",
    body: "Every source, determination, filing and receipt is written to a tamper-evident, hash-chained record as it happens. The audit report is a read of that record, and an independent verifier checks it on a clean machine.",
    points: [
      "Hash-chained evidence: a record changed after the fact fails verification",
      "Signed exports an auditor verifies with a tool that shares no code with Sentience",
      "No report is reconstructed from memory or email",
    ],
  },
] as const;

export const SENTIENCE_TEAM = {
  heading: "It replaces the compliance team, not their spreadsheet",
  body: "Most compliance software is a better spreadsheet: it tracks the work and leaves people to do it. Sentience does the work — the watching, the filing, the flagging and the reporting — and keeps the proof.",
  without: [
    "Someone reads agency bulletins across every state",
    "Someone works out which entities a change reaches",
    "Someone tracks renewal dates in a calendar",
    "Someone prepares and submits each filing",
    "Someone notices a lapsed registration — eventually",
    "Someone rebuilds the evidence when the auditor asks",
  ],
  with: [
    "Sentience watches all 50 states",
    "Sentience determines what applies to each entity",
    "Sentience computes every deadline",
    "Sentience files under the authority you recorded",
    "Sentience flags the lapse the day it shows up",
    "Sentience writes the audit report from the record",
  ],
};

export const SENTIENCE_AUTONOMY = {
  heading: "Autonomous, under authority you set once",
  body: "You decide how far Sentience goes on its own, entity by entity. At full autonomy it files without anyone signing off on each filing, inside the authority you recorded — and it refuses rather than guesses when that authority doesn't cover an action.",
  levels: [
    { name: "Observe", body: "Watches and flags. Takes no action." },
    { name: "Recommend", body: "Prepares every filing for you to send." },
    { name: "Supervised", body: "Files once you approve each one." },
    { name: "Governed", body: "Files on its own under the authority you recorded." },
  ],
};

export const SENTIENCE_PILOT = {
  eyebrow: "Founding Design Partner Program",
  headline: "Put Sentience against the compliance work you already struggle to manage.",
  lede:
    "We are opening a limited number of 30-day regulatory operations pilots for teams with a defined multi-state compliance workflow.",
  scope: ["1–3 legal entities", "3–10 jurisdictions", "One defined class of regulatory obligations"],
  outcome:
    "The objective is to show Sentience doing that work on its own for the selected entities and states: watching the regulations, filing the renewals, flagging the anomalies and writing the audit report.",
  deliverable:
    "At the end of the pilot, you receive a documented view of the changes, determinations, obligations, decisions, actions and evidence generated during the engagement.",
  cta: "Apply for a Founding Pilot",
  formHeading: "Tell us what you need to manage.",
  formLede:
    "We use this information to decide whether the workflow is a good fit for the founding pilot and to scope the first conversation.",
};

export const SENTIENCE_CLOSING = {
  headline: "Stop discovering compliance work after it becomes a problem.",
  body: "Sentience watches the regulations, files the renewals, flags the anomalies and writes the audit report, on its own and with the proof kept.",
  line: "Watches · Files · Flags · Reports",
};

/** Facts the Privacy and Terms pages depend on, as confirmed by the owner. */
export const SENTIENCE_LEGAL = {
  entity: "Odingard Security, a brand of Six Sense Enterprise Services LLC",
  legalEntity: "Six Sense Enterprise Services LLC",
  product: "Sentience",
  email: "privacy@odingard.com",
  jurisdiction: "the State of Texas, United States",
  retention: "24 months from our last contact with you",
  lastUpdated: "30 June 2026",
  collected: [
    "Your name, work email address, organization, and role or title",
    "The compliance details you select: number of legal entities, jurisdictions you need watched, how compliance is handled today, and pilot timing",
    "Any notes you add to the form",
    "Standard server and security logs generated by our hosting provider, including your IP address",
  ],
  subprocessors: [
    { name: "Cloudflare", role: "Hosting, edge security, and the database that stores pilot requests." },
    { name: "Resend", role: "Delivers the internal email notification that a pilot request arrived." },
  ],
} as const;
